// File: webelieve-frontend/src/features/intake/components/ConvertLeadDialog.tsx
// EF-5.2: Lead-to-client conversion dialog with preview, template picker, and post-conversion redirect
// Calls GET /api/v1/intake/clients/:clientId/convert/preview then POST /api/v1/intake/clients/:clientId/convert

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog';
import { apiClient } from '@/config/api';
import {
  intakeCrmApi,
  type ConversionRequest,
  type ConversionResult,
  type ConversionPreview,
  type MatchedTemplate,
} from '../api/intakeCrmApi';

interface ConvertLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
  practiceArea?: string;
  assignedUserId?: string;
  onSuccess: (result: ConversionResult) => void;
}

type DialogPhase = 'loading' | 'preview' | 'converting' | 'success' | 'error';

interface UserOption {
  id: string;
  keycloakUserId?: string;
  name: string;
  email?: string;
  roles: string[];
  isAttorney?: boolean;
}

const readString = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isValidUuid = (value?: string | null): value is string =>
  typeof value === 'string' && UUID_PATTERN.test(value.trim());

const normalizeUserOption = (value: any): UserOption | null => {
  const id = readString(value?.id, value?.user_id, value?.userId);
  if (!id) return null;

  const firstName = readString(value?.firstName, value?.first_name);
  const lastName = readString(value?.lastName, value?.last_name);
  const name = readString(
    value?.name,
    firstName && lastName ? `${firstName} ${lastName}` : undefined,
    value?.email,
    id,
  ) || id;

  const roles = Array.isArray(value?.roles)
    ? value.roles.filter((role: unknown): role is string => typeof role === 'string')
    : [];

  return {
    id,
    keycloakUserId: readString(
      value?.keycloakUserId,
      value?.keycloak_user_id,
      value?.keycloakUserSub,
      value?.keycloak_user_sub,
    ),
    name,
    email: readString(value?.email),
    roles,
    isAttorney: Boolean(
      value?.isAttorney ||
      value?.userType === 'Attorney' ||
      roles.some((role: string) => role.toLowerCase().includes('attorney')),
    ),
  };
};

const mergeUserOptions = (...groups: Array<Array<UserOption | null | undefined>>): UserOption[] => {
  const merged = new Map<string, UserOption>();

  groups.flat().forEach((option) => {
    if (!option?.id) return;

    const existing = merged.get(option.id);
    if (!existing) {
      merged.set(option.id, option);
      return;
    }

    merged.set(option.id, {
      ...existing,
      ...option,
      roles: Array.from(new Set([...(existing.roles || []), ...(option.roles || [])])),
      name: option.name || existing.name,
      email: option.email || existing.email,
      keycloakUserId: option.keycloakUserId || existing.keycloakUserId,
      isAttorney: existing.isAttorney || option.isAttorney,
    });
  });

  return Array.from(merged.values());
};

const parseUserCollection = (payload: any): UserOption[] => {
  const candidates = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.users)
        ? payload.users
        : Array.isArray(payload?.data?.users)
          ? payload.data.users
          : [];

  return candidates
    .map((candidate: any) => normalizeUserOption(candidate))
    .filter((option: UserOption | null): option is UserOption => Boolean(option));
};

const ConvertLeadDialog: React.FC<ConvertLeadDialogProps> = ({
  open,
  onOpenChange,
  clientId,
  clientName,
  practiceArea: defaultPracticeArea,
  assignedUserId,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();

  // Phase state
  const [phase, setPhase] = useState<DialogPhase>('loading');
  const [preview, setPreview] = useState<ConversionPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Form state
  const [practiceArea, setPracticeArea] = useState(defaultPracticeArea || '');
  const [description, setDescription] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [responsibleStaffId, setResponsibleStaffId] = useState('');
  const [responsibleAttorneyId, setResponsibleAttorneyId] = useState('');
  const [billingMethod, setBillingMethod] = useState<string>('Hourly');
  const [priority, setPriority] = useState<string>('medium');
  const [isBillable, setIsBillable] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ConversionResult | null>(null);
  const [staffOptions, setStaffOptions] = useState<UserOption[]>([]);
  const [attorneyOptions, setAttorneyOptions] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);

  const currentUserOption = useMemo<UserOption | null>(() => {
    const currentUserId = readString(keycloak.subject, keycloak.tokenParsed?.sub);
    if (!currentUserId) return null;

    const roles = Array.isArray(keycloak.tokenParsed?.realm_access?.roles)
      ? keycloak.tokenParsed?.realm_access?.roles.filter((role): role is string => typeof role === 'string')
      : [];
    const firstName = readString(keycloak.tokenParsed?.given_name);
    const lastName = readString(keycloak.tokenParsed?.family_name);

    return {
      id: currentUserId,
      keycloakUserId: currentUserId,
      name: readString(
        keycloak.tokenParsed?.name,
        firstName && lastName ? `${firstName} ${lastName}` : undefined,
        keycloak.tokenParsed?.preferred_username,
        keycloak.tokenParsed?.email,
        'Current User',
      ) || 'Current User',
      email: readString(keycloak.tokenParsed?.email),
      roles,
      isAttorney: roles.some((role) => role.toLowerCase().includes('attorney')),
    };
  }, [keycloak.subject, keycloak.tokenParsed]);

  const loadUserOptions = useCallback(async () => {
    if (!open) return;

    setLoadingUsers(true);
    setUsersError(null);

    try {
      const [staffResult, attorneyResult] = await Promise.allSettled([
        apiClient.get<any>('/api/v1/admin/users', { limit: 100, isActive: true }),
        apiClient.get<any>('/api/v1/users/attorneys'),
      ]);

      const loadedStaff =
        staffResult.status === 'fulfilled'
          ? parseUserCollection(staffResult.value)
          : [];
      const loadedAttorneys =
        attorneyResult.status === 'fulfilled'
          ? parseUserCollection(attorneyResult.value)
          : [];

      const fallbackAssignedOption =
        assignedUserId && !loadedStaff.some((option) => option.id === assignedUserId)
          ? {
              id: assignedUserId,
              name: assignedUserId === currentUserOption?.id
                ? currentUserOption.name
                : `Lead assignee (${assignedUserId.slice(0, 8)})`,
              roles: [],
            }
          : null;

      const mergedStaff = mergeUserOptions(
        loadedStaff,
        loadedAttorneys,
        currentUserOption ? [currentUserOption] : [],
        fallbackAssignedOption ? [fallbackAssignedOption] : [],
      );
      const mergedAttorneys = mergeUserOptions(
        loadedStaff.filter((option) => option.isAttorney),
        loadedAttorneys,
        currentUserOption?.isAttorney ? [currentUserOption] : [],
      );

      setStaffOptions(mergedStaff);
      setAttorneyOptions(mergedAttorneys);

      if (!mergedStaff.length && !mergedAttorneys.length) {
        setUsersError('No assignable firm users were returned for this matter conversion.');
      } else if (staffResult.status === 'rejected') {
        setUsersError('Staff list unavailable; using attorney/current-user fallbacks.');
      } else if (attorneyResult.status === 'rejected') {
        setUsersError('Attorney list unavailable; attorney choices may be limited.');
      }
    } catch (loadError) {
      console.error('[ConvertLeadDialog] Failed to load assignment options:', loadError);
      setStaffOptions(currentUserOption ? [currentUserOption] : []);
      setAttorneyOptions(currentUserOption?.isAttorney ? [currentUserOption] : []);
      setUsersError(loadError instanceof Error ? loadError.message : 'Failed to load assignment options');
    } finally {
      setLoadingUsers(false);
    }
  }, [assignedUserId, currentUserOption, open]);

  const defaultResponsibleStaffId = useMemo(() => {
    if (assignedUserId && staffOptions.some((option) => option.id === assignedUserId)) {
      return assignedUserId;
    }
    if (currentUserOption && staffOptions.some((option) => option.id === currentUserOption.id)) {
      return currentUserOption.id;
    }
    return assignedUserId || currentUserOption?.id || staffOptions[0]?.id || '';
  }, [assignedUserId, currentUserOption, staffOptions]);

  const defaultResponsibleAttorneyId = useMemo(() => {
    if (assignedUserId && attorneyOptions.some((option) => option.id === assignedUserId)) {
      return assignedUserId;
    }
    if (currentUserOption?.isAttorney) {
      const currentAttorney = attorneyOptions.find(
        (option) => option.id === currentUserOption.id || option.keycloakUserId === currentUserOption.id,
      );
      if (currentAttorney) return currentAttorney.id;
    }
    return '';
  }, [assignedUserId, attorneyOptions, currentUserOption]);

  // Fetch conversion preview when dialog opens
  const fetchPreview = useCallback(async () => {
    setPhase('loading');
    setPreviewError(null);
    try {
      const p = await intakeCrmApi.getConversionPreview(
        clientId,
        practiceArea || defaultPracticeArea || undefined,
        jurisdiction || undefined,
      );
      setPreview(p);

      // Auto-select recommended template
      if (p.recommendedTemplateId) {
        setSelectedTemplateId(p.recommendedTemplateId);
      } else if (p.matchedTemplates.length === 1) {
        setSelectedTemplateId(p.matchedTemplates[0].templateId);
      } else {
        setSelectedTemplateId('');
      }

      if (!practiceArea.trim() && p.conversionPlan?.recommendedPracticeArea) {
        setPracticeArea(p.conversionPlan.recommendedPracticeArea);
      }

      setPhase('preview');
    } catch (err) {
      console.error('[ConvertLeadDialog] Preview fetch failed:', err);
      setPreviewError(err instanceof Error ? err.message : 'Failed to load preview');
      // Fall back to form without preview data
      setPhase('preview');
    }
  }, [clientId, practiceArea, defaultPracticeArea, jurisdiction]);

  const applyMatterPlan = useCallback((plan: NonNullable<ConversionPreview['conversionPlan']>['potentialMatters'][number]) => {
    setPracticeArea(plan.practiceArea);
    setDescription((current) => current.trim() ? current : plan.summary);
    setJurisdiction(plan.jurisdictionCourtName || plan.jurisdictionCode || plan.jurisdiction || '');
    if (plan.recommendedTemplateId) {
      setSelectedTemplateId(plan.recommendedTemplateId);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setPracticeArea(defaultPracticeArea || '');
      setDescription('');
      setJurisdiction('');
      setResponsibleStaffId(defaultResponsibleStaffId);
      setResponsibleAttorneyId(defaultResponsibleAttorneyId);
      setSelectedTemplateId('');
      setResult(null);
      setError(null);
      fetchPreview();
      loadUserOptions();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;

    if (!responsibleStaffId && defaultResponsibleStaffId) {
      setResponsibleStaffId(defaultResponsibleStaffId);
    }
    if (!responsibleAttorneyId && defaultResponsibleAttorneyId) {
      setResponsibleAttorneyId(defaultResponsibleAttorneyId);
    }
  }, [
    defaultResponsibleAttorneyId,
    defaultResponsibleStaffId,
    open,
    responsibleAttorneyId,
    responsibleStaffId,
  ]);

  // Re-fetch preview when practice area or jurisdiction changes (debounced via blur)
  const handlePracticeAreaBlur = useCallback(() => {
    if (practiceArea.trim() && open && phase === 'preview') {
      fetchPreview();
    }
  }, [practiceArea, open, phase, fetchPreview]);

  const handleJurisdictionBlur = useCallback(() => {
    if (open && phase === 'preview') {
      fetchPreview();
    }
  }, [open, phase, fetchPreview]);

  const handleConvert = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!practiceArea.trim()) {
      setError('Practice area is required');
      return;
    }
    if (!responsibleStaffId.trim()) {
      setError('Responsible staff ID is required');
      return;
    }

    // Check for blocking issues
    if (preview && !preview.isConvertible && preview.blockingIssues.length > 0) {
      setError('Cannot convert: ' + preview.blockingIssues.join('; '));
      return;
    }

    setSaving(true);
    setError(null);
    setPhase('converting');

    try {
      const conversionData: ConversionRequest = {
        practice_area: practiceArea.trim(),
        description: description.trim() || undefined,
        jurisdiction: jurisdiction.trim() || undefined,
        responsible_staff_id: responsibleStaffId.trim(),
        responsible_attorney_id: responsibleAttorneyId.trim() || undefined,
        billing_method: billingMethod || undefined,
        is_billable: isBillable,
        priority: priority || undefined,
        task_template_id: selectedTemplateId || undefined,
      };

      const conversionResult = await intakeCrmApi.convertLead(clientId, conversionData);
      setResult(conversionResult);
      setPhase('success');
      onSuccess(conversionResult);
    } catch (err) {
      console.error('[ConvertLeadDialog] Conversion failed:', err);
      setError(err instanceof Error ? err.message : 'Conversion failed');
      setPhase('preview');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setError(null);
    setPhase('loading');
    onOpenChange(false);
  };

  const handleGoToMatter = () => {
    if (!result) return;
    if (!isValidUuid(result.matterId)) {
      setError('The conversion completed, but EdgeLex did not receive a valid matter ID to open. Refresh the intake dashboard and open the matter from the Matters list.');
      return;
    }

    handleClose();
    navigate(`/matters/${result.matterId}`);
  };

  // --- Success Phase ---
  if (phase === 'success' && result) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Conversion Successful</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-lex-success-bg rounded-lg border border-lex-success/20">
              <svg className="w-8 h-8 text-lex-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-lex-success">
                  {result.clientName} is now a client
                </p>
                <p className="text-xs text-lex-success">
                  Matter #{result.matterNumber} created
                </p>
              </div>
            </div>

            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Practice Area</dt>
                <dd className="text-gray-900 dark:text-white">{result.practiceArea}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Documents Linked</dt>
                <dd className="text-gray-900 dark:text-white">{result.documentsLinked}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Tasks Created</dt>
                <dd className="text-gray-900 dark:text-white">{result.tasksCreated}</dd>
              </div>
              {result.templateUsed && (
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Template Used</dt>
                  <dd className="text-gray-900 dark:text-white truncate ml-4">{result.templateUsed}</dd>
                </div>
              )}
            </dl>

            {result.warnings && result.warnings.length > 0 && (
              <div className="p-2 bg-lex-warning-bg rounded border border-lex-warning/20">
                <p className="text-xs font-medium text-lex-warning mb-1">Warnings</p>
                {result.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-lex-warning">{w}</p>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleGoToMatter}
                disabled={!isValidUuid(result.matterId)}
                className="px-4 py-2 text-sm font-medium text-lex-accent-contrast bg-lex-accent rounded-md hover:bg-lex-accent-hover transition-colors flex items-center gap-1.5"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                Go to Matter
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // --- Loading Phase ---
  if (phase === 'loading') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Preparing Conversion</DialogTitle>
            <DialogDescription>
              Checking readiness for <strong>{clientName}</strong>...
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <svg className="w-8 h-8 text-lex-accent animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loading conversion preview...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // --- Preview + Form Phase ---
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Convert Lead to Client</DialogTitle>
          <DialogDescription>
            Convert <strong>{clientName}</strong> to a paying client with a new Matter.
          </DialogDescription>
        </DialogHeader>

        {/* Pre-conversion Readiness Checklist */}
        {preview && !previewError && (
          <div className="space-y-2 mb-4">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Conversion Readiness
            </h3>

            {/* Blocking issues */}
            {preview.blockingIssues.length > 0 && (
              <div className="p-2.5 bg-lex-danger-bg rounded-lg border border-lex-danger/20">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-lex-danger mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-xs font-medium text-lex-danger">Blocking Issues</p>
                    {preview.blockingIssues.map((issue, i) => (
                      <p key={i} className="text-xs text-lex-danger mt-0.5">{issue}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Checklist items */}
            <div className="grid grid-cols-3 gap-2">
              <ChecklistItem
                label="Status"
                value={preview.clientStatus}
                ok={preview.isConvertible}
              />
              <ChecklistItem
                label="Documents"
                value={`${preview.documentCount} file${preview.documentCount !== 1 ? 's' : ''}`}
                ok={preview.documentCount > 0}
                neutral={preview.documentCount === 0}
              />
              <ChecklistItem
                label="Completeness"
                value={`${preview.intakeCompleteness}%`}
                ok={preview.intakeCompleteness >= 70}
                neutral={preview.intakeCompleteness >= 40 && preview.intakeCompleteness < 70}
              />
            </div>

            {preview.conversionPlan && (
              <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Conversion Strategy
                    </p>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {preview.conversionPlan.splitRecommended
                        ? 'This lead likely represents multiple matters that share one client profile.'
                        : 'This lead looks ready for a single primary matter conversion.'}
                    </p>
                  </div>
                  {preview.conversionPlan.recommendedPracticeArea && (
                    <span className="px-2 py-1 text-xs rounded-full bg-lex-accent-soft text-lex-accent">
                      Recommended: {preview.conversionPlan.recommendedPracticeArea}
                    </span>
                  )}
                </div>

                {preview.conversionPlan.potentialMatters.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {preview.conversionPlan.potentialMatters.map((plan) => (
                      <button
                        key={`${plan.practiceArea}-${plan.title}`}
                        type="button"
                        onClick={() => applyMatterPlan(plan)}
                        className="w-full text-left rounded-md border border-gray-200 dark:border-gray-700 p-3 hover:border-lex-accent hover:bg-blue-50/40 dark:hover:bg-gray-900 transition-colors"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{plan.title}</p>
                            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">{plan.summary}</p>
                            {(plan.jurisdictionCourtName || plan.jurisdictionCode || plan.jurisdiction) && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Jurisdiction: {plan.jurisdictionCourtName || plan.jurisdictionCode || plan.jurisdiction}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{plan.matchedTemplateCount} templates</p>
                            {plan.recommendedTemplateId && (
                              <p className="text-xs text-lex-accent mt-1">recommended template ready</p>
                            )}
                          </div>
                        </div>
                        {plan.missingFacts.length > 0 && (
                          <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                            Missing: {plan.missingFacts.join(', ')}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {preview.conversionPlan.nextActions.length > 0 && (
                  <ul className="mt-3 list-disc list-inside text-xs text-gray-600 dark:text-gray-300 space-y-1">
                    {preview.conversionPlan.nextActions.map((action, index) => (
                      <li key={index}>{action}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {/* Preview fetch error — non-blocking, just informational */}
        {previewError && (
          <div className="mb-3 p-2 bg-lex-warning-bg rounded border border-lex-warning/20">
            <p className="text-xs text-lex-warning">
              Could not load preview: {previewError}. You can still proceed.
            </p>
          </div>
        )}

        <form onSubmit={handleConvert} className="space-y-3">
          {error && (
            <div className="text-sm text-lex-danger bg-lex-danger-bg rounded p-2">
              {error}
            </div>
          )}

          {/* Practice Area (required) */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Practice Area <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={practiceArea}
              onChange={e => setPracticeArea(e.target.value)}
              onBlur={handlePracticeAreaBlur}
              placeholder="e.g. Family Law, Personal Injury"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
            />
          </div>

          {/* Task Template Picker */}
          {preview && preview.matchedTemplates.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Task Template
              </label>
              <select
                value={selectedTemplateId}
                onChange={e => setSelectedTemplateId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent"
              >
                <option value="">No template (create matter only)</option>
                {preview.matchedTemplates.map((t) => (
                  <option key={t.templateId} value={t.templateId}>
                    {t.name} ({t.taskCount} tasks){t.jurisdiction ? ` — ${t.jurisdiction}` : ''}
                    {t.templateId === preview.recommendedTemplateId ? ' (Recommended)' : ''}
                  </option>
                ))}
              </select>
              {selectedTemplateId && (
                <TemplateDetail template={preview.matchedTemplates.find(t => t.templateId === selectedTemplateId)} />
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Matter Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of the matter..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
            />
          </div>

          {/* Jurisdiction + Priority */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Jurisdiction</label>
              <input
                type="text"
                value={jurisdiction}
                onChange={e => setJurisdiction(e.target.value)}
                onBlur={handleJurisdictionBlur}
                placeholder="e.g. CA, NY"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Priority</label>
              <select
                value={priority}
                onChange={e => setPriority(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Assignment */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Responsible Staff <span className="text-red-500">*</span>
            </label>
            <select
              value={responsibleStaffId}
              onChange={e => setResponsibleStaffId(e.target.value)}
              disabled={loadingUsers}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
            >
              <option value="">
                {loadingUsers ? 'Loading firm users...' : 'Select responsible staff'}
              </option>
              {staffOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}{option.email ? ` (${option.email})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Responsible Attorney
            </label>
            <select
              value={responsibleAttorneyId}
              onChange={e => setResponsibleAttorneyId(e.target.value)}
              disabled={loadingUsers}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
            >
              <option value="">
                {loadingUsers ? 'Loading attorneys...' : 'Select responsible attorney (optional)'}
              </option>
              {attorneyOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}{option.email ? ` (${option.email})` : ''}
                </option>
              ))}
            </select>
          </div>

          {usersError && (
            <div className="text-xs text-lex-warning bg-lex-warning-bg rounded p-2 border border-lex-warning/20">
              {usersError}
            </div>
          )}

          {/* Billing */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Billing Method</label>
              <select
                value={billingMethod}
                onChange={e => setBillingMethod(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent"
              >
                <option value="Hourly">Hourly</option>
                <option value="Flat">Flat Fee</option>
                <option value="Contingency">Contingency</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 pb-2">
                <input
                  type="checkbox"
                  checked={isBillable}
                  onChange={e => setIsBillable(e.target.checked)}
                  className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Billable</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
            <DialogClose asChild>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={saving || !practiceArea.trim() || !responsibleStaffId.trim() || (preview?.isConvertible === false)}
              className="px-4 py-2 text-sm font-medium text-lex-accent-contrast bg-lex-accent rounded-md hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
            >
              {saving ? (
                <>
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Converting...
                </>
              ) : (
                'Convert to Client'
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- Sub-components ---

function ChecklistItem({
  label,
  value,
  ok,
  neutral,
}: {
  label: string;
  value: string;
  ok: boolean;
  neutral?: boolean;
}) {
  const color = ok
    ? 'text-lex-success'
    : neutral
      ? 'text-lex-warning'
      : 'text-lex-danger';

  const icon = ok ? (
    <svg className="w-3.5 h-3.5 text-lex-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ) : neutral ? (
    <svg className="w-3.5 h-3.5 text-lex-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01" />
    </svg>
  ) : (
    <svg className="w-3.5 h-3.5 text-lex-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  return (
    <div className="flex items-center gap-1.5 p-2 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
      {icon}
      <div className="min-w-0">
        <p className={`text-sm font-semibold ${color}`}>{value}</p>
        <p className="text-[10px] text-gray-400 dark:text-gray-500">{label}</p>
      </div>
    </div>
  );
}

function TemplateDetail({ template }: { template?: MatchedTemplate }) {
  if (!template) return null;
  return (
    <div className="mt-1.5 p-2 bg-lex-accent-soft rounded border border-lex-accent">
      <p className="text-xs text-lex-accent">
        <span className="font-medium">{template.taskCount} tasks</span> will be created
        {template.jurisdiction && <> for <span className="font-medium">{template.jurisdiction}</span></>}
      </p>
      {template.description && (
        <p className="text-xs text-lex-accent mt-0.5">{template.description}</p>
      )}
    </div>
  );
}

export default ConvertLeadDialog;
